import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, UserCheck, Footprints, MapPinned, FileText, Camera, CheckCircle2, XCircle, CalendarRange, PartyPopper, Plus, X, Radar } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import TenantPortal from "./TenantPortal";

const SEVERITY_COLOR = { info: "#2563eb", warning: "#d97706", critical: "#BF3437" };
const SEVERITY_LABEL = { info: "Información", warning: "Atención", critical: "Urgente" };

export const fileUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);
const fmt = (d) => (d ? new Date(d).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function ClientePortal() {
  const { user } = useAuth();
  const isClienteAdmin = user?.role === "cliente_admin";
  const isInquilino = user?.role === "inquilino";
  const [objetivos, setObjetivos] = useState(null);
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [tab, setTab] = useState("turnos");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cronoMonth, setCronoMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/objetivos");
        setObjetivos(data);
        if (data.length > 0) setClientId(data[0].client_id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const current = useMemo(() => objetivos?.find((o) => o.client_id === clientId), [objetivos, clientId]);

  const TABS = [
    { key: "turnos", label: "Turnos de Servicio", icon: Clock },
    { key: "cronograma", label: "Cronograma", icon: CalendarRange },
    { key: "autorizados", label: "Personal Autorizado", icon: UserCheck },
    ...(isClienteAdmin ? [{ key: "visitas", label: "Visitas e Ingresos", icon: Footprints }] : []),
    { key: "rondas", label: "Rondas Realizadas", icon: MapPinned },
    ...(isClienteAdmin ? [{ key: "ronda_vivo", label: "Ronda en Vivo", icon: Radar }] : []),
    ...(isClienteAdmin ? [{ key: "novedades", label: "Novedades", icon: FileText }] : []), // Fix BQ: solo administración
    ...(current?.sum_enabled ? [{ key: "sum", label: `Reservar ${current.sum_name || "SUM"}`, icon: PartyPopper }] : []),
  ];

  if (objetivos === null) return <div style={{ padding: 20 }}><div className="skel" style={{ height: 40 }} /></div>;

  if (objetivos.length === 0) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)" }} data-testid="portal-no-access">
        Todavía no tenés objetivos asignados. Comunicate con INVICT para que te habiliten el acceso.
      </div>
    );
  }

  if (isInquilino) return <TenantPortal objetivos={objetivos} />;

  return (
    <div style={{ padding: "10px 20px 40px", maxWidth: 1100, margin: "0 auto" }} data-testid="cliente-portal-page">
      <div className="card" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
        <div className="field" style={{ margin: 0, minWidth: 200 }}>
          <label>Objetivo</label>
          <select value={clientId} onChange={(e) => { setClientId(e.target.value); setSiteId(""); }} data-testid="portal-select-objetivo">
            {objetivos.map((o) => <option key={o.client_id} value={o.client_id}>{o.client_name}</option>)}
          </select>
        </div>
        {current?.sites?.length > 0 && (
          <div className="field" style={{ margin: 0, minWidth: 160 }}>
            <label>Sede</label>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="portal-select-sede">
              <option value="">Todas las sedes</option>
              {current.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        {tab !== "autorizados" && tab !== "cronograma" && tab !== "ronda_vivo" && (
          <>
            <div className="field" style={{ margin: 0 }}><label>Desde</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="portal-date-from" /></div>
            <div className="field" style={{ margin: 0 }}><label>Hasta</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="portal-date-to" /></div>
          </>
        )}
        {tab === "cronograma" && (
          <div className="field" style={{ margin: 0 }}><label>Mes</label><input type="month" value={cronoMonth} onChange={(e) => setCronoMonth(e.target.value)} data-testid="portal-cronograma-month" /></div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 8, border: "none",
              background: tab === t.key ? "#0A0A0A" : "#fff", color: tab === t.key ? "#FFFBF4" : "#0A0A0A",
              fontWeight: 800, fontSize: 12, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "turnos" && <TurnosTab clientId={clientId} siteId={siteId} dateFrom={dateFrom} dateTo={dateTo} />}
      {tab === "cronograma" && <CronogramaTab clientId={clientId} siteId={siteId} month={cronoMonth} />}
      {tab === "autorizados" && <AutorizadosTab clientId={clientId} />}
      {tab === "visitas" && <VisitasTab clientId={clientId} siteId={siteId} dateFrom={dateFrom} dateTo={dateTo} />}
      {tab === "rondas" && <RondasTab clientId={clientId} siteId={siteId} dateFrom={dateFrom} dateTo={dateTo} />}
      {tab === "ronda_vivo" && <RondaVivoTab clientId={clientId} siteId={siteId} />}
      {tab === "novedades" && isClienteAdmin && <NovedadesTab clientId={clientId} siteId={siteId} dateFrom={dateFrom} dateTo={dateTo} />}
      {tab === "sum" && <SumTab clientId={clientId} siteId={siteId} sumName={current?.sum_name || "SUM"} isClienteAdmin={isClienteAdmin} userId={user?.id} />}
    </div>
  );
}

export function EmptyState({ children, ...rest }) {
  return <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)" }} {...rest}>{children}</div>;
}

function useList(endpoint, params) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!params.client_id) return;
    setRows(null);
    (async () => {
      try { const { data } = await api.get(endpoint, { params }); setRows(data); }
      catch (e) { toast.error(formatApiError(e)); setRows([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(params)]);
  return rows;
}

function TurnosTab({ clientId, siteId, dateFrom, dateTo }) {
  const rows = useList("/portal/turnos", { client_id: clientId, site_id: siteId || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined });
  if (rows === null) return <div className="skel" style={{ height: 80 }} />;
  if (rows.length === 0) return <EmptyState>Sin turnos de servicio registrados en este período.</EmptyState>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="portal-turnos-list">
      {rows.map((r) => (
        <div key={r.id} className="card" data-testid={`portal-turno-${r.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 13 }}>{r.supervisor_name || "—"}{r.site_name ? ` · ${r.site_name}` : ""}</div>
              <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Desde {fmt(r.opened_at)} {r.closed_at ? `hasta ${fmt(r.closed_at)}` : "(en curso)"}</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: r.status === "open" ? "#16a34a" : "var(--invict-text-muted)" }}>{r.status === "open" ? "En curso" : "Cerrado"}</span>
          </div>
          {r.notes && <div style={{ fontSize: 12, marginTop: 6, color: "var(--invict-text-muted)" }}>{r.notes}</div>}
        </div>
      ))}
    </div>
  );
}

function CronogramaTab({ clientId, siteId, month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!clientId || !month) return;
    setData(null);
    (async () => {
      try {
        const { data: d } = await api.get("/portal/cronograma", { params: { client_id: clientId, site_id: siteId || undefined, month } });
        setData(d);
      } catch (e) { toast.error(formatApiError(e)); setData({ assignments: [], total_hours: 0 }); }
    })();
  }, [clientId, siteId, month]);

  if (data === null) return <div className="skel" style={{ height: 80 }} />;
  return (
    <div data-testid="portal-cronograma-list">
      {data.total_hours != null && (
        <div className="card" style={{ padding: 14, marginBottom: 12, background: "#0A0A0A", color: "#FFFBF4", display: "flex", justifyContent: "space-between" }} data-testid="portal-cronograma-total">
          <span style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,251,244,0.6)" }}>Total del mes</span>
          <span style={{ fontSize: 18, fontWeight: 900 }}>{data.total_hours} h</span>
        </div>
      )}
      {data.assignments.length === 0 ? <EmptyState>Sin turnos programados en este período.</EmptyState> : (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.assignments.map((r) => (
          <div key={r.id} className="card" data-testid={`portal-cronograma-${r.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{r.user_name}{r.site_name ? ` · ${r.site_name}` : ""}</div>
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{r.date} · {r.time_from}–{r.time_to}</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 800 }}>{r.hours ?? "—"} h</span>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

export function AutorizadosTab({ clientId }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", dni: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setRows(null);
    try { const { data } = await api.get("/portal/autorizados", { params: { client_id: clientId } }); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); setRows([]); }
  };
  useEffect(() => { if (clientId) load(); }, [clientId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.dni.trim()) return toast.error("Completá nombre, apellido y DNI");
    setBusy(true);
    try {
      await api.post("/portal/autorizados", { client_id: clientId, name: form.name, surname: form.surname, dni: form.dni, notes: form.notes || null });
      toast.success("Persona autorizada agregada");
      setForm({ name: "", surname: "", dni: "", notes: "" }); setCreating(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button onClick={() => setCreating((v) => !v)} className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }} data-testid="portal-autorizado-add-btn">
          {creating ? <><X size={13} style={{ marginRight: 6 }}/>Cancelar</> : <><Plus size={13} style={{ marginRight: 6 }}/>Nuevo autorizado</>}
        </button>
      </div>
      {creating && (
        <form onSubmit={submit} className="card" style={{ marginBottom: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} data-testid="portal-autorizado-form">
          <div className="field" style={{ margin: 0 }}><label>Nombre *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="portal-autorizado-name" /></div>
          <div className="field" style={{ margin: 0 }}><label>Apellido *</label><input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="portal-autorizado-surname" /></div>
          <div className="field" style={{ margin: 0 }}><label>DNI *</label><input value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} data-testid="portal-autorizado-dni" /></div>
          <div className="field" style={{ margin: 0 }}><label>Notas</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="ej. empleada doméstica, cuidador" data-testid="portal-autorizado-notes" /></div>
          <button type="submit" disabled={busy} className="btn-primary" style={{ gridColumn: "1 / -1" }} data-testid="portal-autorizado-submit">{busy ? "Guardando..." : "Guardar"}</button>
        </form>
      )}
      {rows === null ? <div className="skel" style={{ height: 80 }} /> : rows.length === 0 ? <EmptyState>Sin personal autorizado registrado.</EmptyState> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="portal-autorizados-list">
          {rows.map((r) => (
            <div key={r.id} className="card" style={{ display: "flex", gap: 12, alignItems: "center" }} data-testid={`portal-autorizado-${r.id}`}>
              {fileUrl(r.photo_url) ? <img src={fileUrl(r.photo_url)} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--invict-bone-3)" }} />}
              <div>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{r.name} {r.surname}</div>
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>DNI {r.dni}{r.notes ? ` · ${r.notes}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisitasTab({ clientId, siteId, dateFrom, dateTo }) {
  const rows = useList("/portal/visitas", { client_id: clientId, site_id: siteId || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined });
  if (rows === null) return <div className="skel" style={{ height: 80 }} />;
  if (rows.length === 0) return <EmptyState>Sin visitas o ingresos registrados en este período.</EmptyState>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="portal-visitas-list">
      {rows.map((r) => (
        <div key={r.id} className="card" data-testid={`portal-visita-${r.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800, fontSize: 13 }}>{r.name} {r.surname}{r.company ? ` (${r.company})` : ""}</span>
              {r.is_authorized === true && <span className="badge green" data-testid={`portal-visita-auth-${r.id}`}>Autorizado{r.authorized_by_name ? ` · ${r.authorized_by_name}` : ""}</span>}
              {r.is_authorized === false && <span className="badge red" data-testid={`portal-visita-auth-${r.id}`}>NO AUTORIZADO</span>}
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: r.direction === "ingreso" ? "#16a34a" : "#BF3437" }}>{r.direction === "ingreso" ? "Ingreso" : "Egreso"}</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 2 }}>
            {r.reason || r.visit_type}{r.site_name ? ` · ${r.site_name}` : ""} · {fmt(r.created_at)} · registrado por {r.user_name}
          </div>
          {r.visit_type === "correo" && r.recipient_dwelling_label && (
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>
              Destinatario: {r.recipient_dwelling_label} ({r.recipient_name}) · {r.picked_up ? "Retirado" : "Pendiente de retiro"}
            </div>
          )}
          {fileUrl(r.photo_url) && <a href={fileUrl(r.photo_url)} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, marginTop: 6, color: "#0A0A0A" }}><Camera size={12} /> Ver foto</a>}
        </div>
      ))}
    </div>
  );
}

function RondasTab({ clientId, siteId, dateFrom, dateTo }) {
  const rows = useList("/portal/rondas", { client_id: clientId, site_id: siteId || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined });
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);

  const open = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id); setDetail(null);
    try { const { data } = await api.get(`/portal/rondas/${id}`); setDetail(data); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (rows === null) return <div className="skel" style={{ height: 80 }} />;
  if (rows.length === 0) return <EmptyState>Sin rondas realizadas en este período.</EmptyState>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="portal-rondas-list">
      {rows.map((r) => (
        <div key={r.id} className="card" data-testid={`portal-ronda-${r.id}`}>
          <button onClick={() => open(r.id)} style={{ width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{r.vigilador_name}{r.site_name ? ` · ${r.site_name}` : ""}</div>
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{fmt(r.scheduled_for)}</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 800 }}>{r.points_completed ?? 0}/{r.total_points ?? 0} puntos</span>
            </div>
          </button>
          {openId === r.id && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--invict-bone-3)" }}>
              {!detail ? <div className="skel" style={{ height: 40 }} /> : (
                <div>
                  {detail.points.map((p) => (
                    <div key={p.order} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "3px 0" }}>
                      {p.cumplido ? <CheckCircle2 size={13} color="#16a34a" /> : <XCircle size={13} color="#BF3437" />}
                      {p.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const liveIcon = L.divIcon({
  className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;background:#16a34a;border:3px solid #FFFBF4;border-radius:50%;box-shadow:0 0 0 1px #16a34a;animation: pulse-green 1.6s infinite;"></div>`,
});
const pointDoneIcon = L.divIcon({
  className: "", iconSize: [16, 16], iconAnchor: [8, 8],
  html: `<div style="width:16px;height:16px;background:#2563eb;border:2px solid #FFFBF4;border-radius:50%;"></div>`,
});
const BA_FALLBACK = [-32.8908, -68.8272]; // Mendoza, fallback si todavía no hay ninguna posición

function RondaVivoTab({ clientId, siteId }) {
  const [rows, setRows] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get("/portal/rondas-vivo", { params: { client_id: clientId, site_id: siteId || undefined } });
      setRows(data.patrols || []);
    } catch (e) { toast.error(formatApiError(e)); setRows([]); }
  };
  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [clientId, siteId]);

  if (rows === null) return <div className="skel" style={{ height: 200 }} />;
  if (rows.length === 0) return <EmptyState data-testid="portal-ronda-vivo-empty">No hay rondas en curso en este momento.</EmptyState>;

  const active = rows.find((r) => r.patrol_id === selectedId) || rows[0];
  const track = active.track || [];
  const last = track[track.length - 1];
  const center = last ? [last.latitude, last.longitude] : BA_FALLBACK;
  const scansWithGps = (active.scans || []).filter((s) => s.latitude != null && s.longitude != null);

  return (
    <div data-testid="portal-ronda-vivo-tab">
      {rows.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {rows.map((r) => (
            <button key={r.patrol_id} onClick={() => setSelectedId(r.patrol_id)} data-testid={`portal-ronda-vivo-select-${r.patrol_id}`}
              className={active.patrol_id === r.patrol_id ? "btn-primary" : "btn-ghost"} style={{ padding: "6px 12px", fontSize: 12 }}>
              {r.vigilador_name}{r.site_name ? ` · ${r.site_name}` : ""}
            </button>
          ))}
        </div>
      )}
      <div className="card" style={{ padding: 14, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }} data-testid={`portal-ronda-vivo-info-${active.patrol_id}`}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{active.vigilador_name}{active.site_name ? ` · ${active.site_name}` : ""}</div>
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Ronda en curso desde {fmt(active.started_at)}</div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 800, padding: "6px 12px", borderRadius: 6, background: "#0A0A0A", color: "#FFFBF4" }} data-testid={`portal-ronda-vivo-progress-${active.patrol_id}`}>
          {active.points_completed}/{active.total_points} puntos
        </span>
      </div>
      <div className="card" style={{ padding: 0, overflow: "hidden", height: 420 }} data-testid="portal-ronda-vivo-map">
        <MapContainer center={center} zoom={16} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {track.length > 1 && (
            <Polyline positions={track.map((t) => [t.latitude, t.longitude])} pathOptions={{ color: "#2563eb", dashArray: "6 6", weight: 3 }} />
          )}
          {last && (
            <Marker position={[last.latitude, last.longitude]} icon={liveIcon}>
              <Popup>{active.vigilador_name} · última posición</Popup>
            </Marker>
          )}
          {scansWithGps.map((s, i) => (
            <Marker key={i} position={[s.latitude, s.longitude]} icon={pointDoneIcon}>
              <Popup>{s.patrol_point_name} · {fmt(s.scanned_at)}</Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {!last && (
        <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 8 }} data-testid="portal-ronda-vivo-no-gps">
          El vigilador todavía no reportó posición GPS en esta ronda.
        </div>
      )}
    </div>
  );
}


function NovedadesTab({ clientId, siteId, dateFrom, dateTo }) {
  const rows = useList("/portal/novedades", { client_id: clientId, site_id: siteId || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined });
  if (rows === null) return <div className="skel" style={{ height: 80 }} />;
  if (rows.length === 0) return <EmptyState>Sin novedades en este período.</EmptyState>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="portal-novedades-list">
      {rows.map((r) => (
        <div key={r.id} className="card" style={{ borderLeft: `4px solid ${SEVERITY_COLOR[r.severity] || "#666"}` }} data-testid={`portal-novedad-${r.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>{r.title}</div>
            <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: SEVERITY_COLOR[r.severity] }}>{SEVERITY_LABEL[r.severity] || r.severity}</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "4px 0" }}>{r.description}</div>
          <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{r.site_name ? `${r.site_name} · ` : ""}{fmt(r.created_at)} · {r.supervisor_name}</div>
        </div>
      ))}
    </div>
  );
}

const SUM_STATUS_LABEL = { confirmada: "Confirmada", pendiente: "Pendiente de aprobación", rechazada: "Rechazada", cancelada: "Cancelada" };
const SUM_STATUS_COLOR = { confirmada: "#16a34a", pendiente: "#d97706", rechazada: "#BF3437", cancelada: "#6B655A" };

export function SumTab({ clientId, siteId, sumName, isClienteAdmin, userId }) {
  const monthKey = (d) => d.toISOString().slice(0, 7);
  const [month, setMonth] = useState(monthKey(new Date()));
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ date: "", time_from: "", time_to: "", unit: "", guests_count: "", reason: "" });
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    if (!clientId) return;
    setRows(null);
    try {
      const { data } = await api.get("/portal/sum-reservations", { params: { client_id: clientId, site_id: siteId || undefined, month } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); setRows([]); }
  };
  useEffect(() => { load(); }, [clientId, siteId, month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(monthKey(new Date(y, m - 1 + delta, 1)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time_from || !form.time_to || !form.unit.trim()) return toast.error("Completá fecha, horario y unidad/departamento");
    setBusy("create");
    try {
      await api.post("/portal/sum-reservations", {
        client_id: clientId, site_id: siteId || null, date: form.date, time_from: form.time_from, time_to: form.time_to,
        unit: form.unit, guests_count: Number(form.guests_count) || 0, reason: form.reason || null,
      });
      toast.success("Reserva enviada");
      setForm({ date: "", time_from: "", time_to: "", unit: "", guests_count: "", reason: "" }); setCreating(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(null); }
  };

  const cancel = async (rid) => {
    if (!window.confirm("¿Cancelar esta reserva?")) return;
    setBusy(rid);
    try { await api.delete(`/portal/sum-reservations/${rid}`); toast.success("Reserva cancelada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const decide = async (rid, approve, reason) => {
    setBusy(rid);
    try {
      await api.patch(`/portal/sum-reservations/${rid}/decision`, { approve, reason });
      toast.success(approve ? "Reserva aprobada" : "Reserva rechazada");
      setRejecting(null); setRejectReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="portal-sum-tab">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => shiftMonth(-1)} className="btn-ghost" style={{ padding: "6px 10px" }} data-testid="portal-sum-month-prev">←</button>
          <span style={{ fontSize: 13, fontWeight: 800, minWidth: 90, textAlign: "center" }}>{month}</span>
          <button onClick={() => shiftMonth(1)} className="btn-ghost" style={{ padding: "6px 10px" }} data-testid="portal-sum-month-next">→</button>
        </div>
        <button onClick={() => setCreating((v) => !v)} className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }} data-testid="portal-sum-add-btn">
          {creating ? <><X size={13} style={{ marginRight: 6 }}/>Cancelar</> : <><Plus size={13} style={{ marginRight: 6 }}/>Reservar {sumName}</>}
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="card" style={{ marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} data-testid="portal-sum-form">
          <div className="field" style={{ margin: 0 }}><label>Fecha *</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="portal-sum-date" /></div>
          <div className="field" style={{ margin: 0 }}><label>Unidad/Departamento *</label><input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="ej. 4to B" data-testid="portal-sum-unit" /></div>
          <div className="field" style={{ margin: 0 }}><label>Desde *</label><input type="time" value={form.time_from} onChange={(e) => setForm({ ...form, time_from: e.target.value })} data-testid="portal-sum-time-from" /></div>
          <div className="field" style={{ margin: 0 }}><label>Hasta *</label><input type="time" value={form.time_to} onChange={(e) => setForm({ ...form, time_to: e.target.value })} data-testid="portal-sum-time-to" /></div>
          <div className="field" style={{ margin: 0 }}><label>Invitados estimados</label><input type="number" min="0" value={form.guests_count} onChange={(e) => setForm({ ...form, guests_count: e.target.value })} data-testid="portal-sum-guests" /></div>
          <div className="field" style={{ margin: 0 }}><label>Motivo</label><input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="ej. cumpleaños, reunión" data-testid="portal-sum-reason" /></div>
          <button type="submit" disabled={busy === "create"} className="btn-primary" style={{ gridColumn: "1 / -1" }} data-testid="portal-sum-submit">{busy === "create" ? "Enviando..." : "Reservar"}</button>
        </form>
      )}

      {rows === null ? <div className="skel" style={{ height: 80 }} /> : rows.length === 0 ? <EmptyState>Sin reservas este mes.</EmptyState> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="portal-sum-list">
          {rows.map((r) => (
            <div key={r.id} className="card" data-testid={`portal-sum-${r.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{r.date} · {r.time_from}–{r.time_to} · Unidad {r.unit}</div>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: SUM_STATUS_COLOR[r.status] }}>{SUM_STATUS_LABEL[r.status]}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "4px 0" }}>{r.requested_by_name} · {r.guests_count} invitados{r.reason ? ` · ${r.reason}` : ""}</div>
              {r.status === "rechazada" && r.reject_reason && <div style={{ fontSize: 11, color: "#BF3437" }}>Motivo del rechazo: {r.reject_reason}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                {isClienteAdmin && r.status === "pendiente" && (
                  <>
                    <button onClick={() => decide(r.id, true)} disabled={busy === r.id} className="btn-primary" style={{ padding: "6px 12px", fontSize: 11 }} data-testid={`portal-sum-approve-${r.id}`}>Aprobar</button>
                    <button onClick={() => setRejecting(r.id)} disabled={busy === r.id} className="btn-ghost" style={{ padding: "6px 12px", fontSize: 11, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`portal-sum-reject-${r.id}`}>Rechazar</button>
                  </>
                )}
                {(isClienteAdmin || r.created_by === userId) && (r.status === "pendiente" || r.status === "confirmada") && (
                  <button onClick={() => cancel(r.id)} disabled={busy === r.id} className="btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} data-testid={`portal-sum-cancel-${r.id}`}>Cancelar</button>
                )}
              </div>
              {rejecting === r.id && (
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motivo del rechazo (opcional)" style={{ flex: 1 }} data-testid={`portal-sum-reject-reason-${r.id}`} />
                  <button onClick={() => decide(r.id, false, rejectReason)} disabled={busy === r.id} className="btn-primary" style={{ padding: "6px 12px", fontSize: 11, background: "#BF3437" }} data-testid={`portal-sum-reject-confirm-${r.id}`}>Confirmar</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
